import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import ProcessSection from './ProcessSection';
import OptimizedImage from './OptimizedImage';
import { IMAGE_BLURS } from '../data/imageBlurs';

const TechnologyPage: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <main className="w-full bg-canvas text-text-primary">
            {/* Hero Section - with image */}
            <section id="hero" className="relative w-full h-screen flex flex-col justify-center items-start px-6 md:px-16 lg:px-24 bg-black text-white">
                {/* Background Image */}
                <OptimizedImage
                    src="/images/mockup_hand.webp"
                    blurSrc={IMAGE_BLURS.mockup_hand}
                    alt="Nura app on phone"
                    priority={true}
                    containerClassName="absolute inset-0 w-full h-full z-0"
                    className="absolute inset-0 w-full h-full object-cover object-[60%] md:object-center"
                />
                {/* Text content */}
                <div className="relative z-10">
                    <h1 className="text-5xl md:text-7xl lg:text-8xl font-normal tracking-[-0.02em] text-white leading-[1.1] max-w-5xl">
                        How Nura<br className="md:hidden" /> works
                    </h1>
                </div>
            </section>

            {/* Section 1 */}
            <section className="relative w-full min-h-screen flex flex-col justify-center bg-canvas py-24 md:py-32">
                <div className="w-full px-6 md:px-24 lg:px-32">
                    {/* Text Area */}
                    <div>
                        <h2 className="text-5xl md:text-7xl lg:text-8xl font-normal tracking-[-0.02em] text-text-primary mb-6 md:mb-8 leading-[1.1]">
                            The first adaptive<br />neural state-optimization engine
                        </h2>
                        <p className="text-lg md:text-xl text-text-primary max-w-2xl">
                            Nura turns your current state into measurable, science-backed, cognitive metrics. Get short practices and insights to bridge the gap between where you are and where you need to be.
                        </p>
                    </div>
                </div>
            </section>

            {/* How It Works Process - Sticky Scroll */}
            <ProcessSection />

            {/* Section 2 - Science (Restored) */}
            <section className="relative w-full min-h-screen flex flex-col justify-center bg-canvas text-text-primary py-24 md:py-32">
                <div className="w-full px-6 md:px-24 lg:px-32 flex flex-col md:flex-row items-center gap-12 md:gap-16">
                    {/* Text Area */}
                    <div className="flex-1">
                        <h2 className="text-5xl md:text-7xl lg:text-8xl font-normal tracking-[-0.02em] text-text-primary mb-6 md:mb-8 leading-[1.1]">
                            Backed by neuroscience research
                        </h2>
                        <p className="text-lg md:text-xl text-text-primary max-w-2xl">
                            Nura analyzes your state based on neuroscience research, to provide you with a unique adapted session every time.
                        </p>
                        <Link
                            to="/research"
                            className="mt-6 md:mt-8 inline-block text-lg text-text-primary whitespace-nowrap bg-transparent border border-primary px-5 py-2 rounded-full hover:bg-text-primary hover:text-canvas transition-colors focus-visible:outline-2 focus-visible:outline-text-primary focus-visible:outline-offset-4 focus:outline-none"
                        >
                            Explore Research
                        </Link>
                    </div>

                    {/* Image */}
                    <div
                        className="relative w-[100vw] md:w-full md:max-w-[600px] -mx-6 md:mx-0 aspect-square flex-shrink-0 rounded-none md:rounded-2xl overflow-hidden isolate"
                        style={{ transform: 'translateZ(0)', WebkitMaskImage: '-webkit-radial-gradient(white, black)' }}
                    >
                        <OptimizedImage
                            src="/images/research_visualization.webp"
                            mobileSrc="/images/research_visualization-mobile.webp"
                            blurSrc={IMAGE_BLURS.research_visualization}
                            alt="Neuroscience research visualization"
                            containerClassName="w-full h-full"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                    </div>
                </div>
            </section>
        </main>
    );
};

export default TechnologyPage;
